import React from "react";
import Modal from "react-modal";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  resetShowSetLocationModal,
  setDumpsterLocation,
} from "../../dumpstersSlice";
import { getDumpYardTemplates } from "../../../DumpYards/dumpYardsSlice";
import { getOrders } from "../../../Orders/ordersSlice";
import { ReactComponent as Close } from "../../../../assets/images/close.svg";
import { DumpsterLocationType, OrderStatus } from "../../../../models";

import "./SetLocationModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

export function SetLocationModal() {
  const dispatch = useAppDispatch();

  const dumpster = useAppSelector(
    (state) => state.dumpsters.showSetLocationModal
  );
  const setLocationAPI = useAppSelector(
    (state) => state.dumpsters.setDumpsterLocation
  );

  // Storage yards
  const templates = useAppSelector((state) => state.dumpYards.templates) || [];
  const storageYards = React.useMemo(
    () => templates.filter((t) => t.isStorage),
    [templates]
  );

  // Orders with dumpster on site (delivered or scheduled for pickup)
  const orders = useAppSelector((state) => state.orders.orders) || [];
  const activeOrders = React.useMemo(
    () =>
      orders.filter(
        (o) =>
          o.status === OrderStatus.OrderStatusDelivered ||
          o.status === OrderStatus.OrderStatusScheduledToPickup
      ),
    [orders]
  );

  const [locationType, setLocationType] =
    React.useState<DumpsterLocationType>("unknown");
  const [selectedOrderId, setSelectedOrderId] = React.useState<string>("");
  const [selectedStorageId, setSelectedStorageId] = React.useState<string>("");

  // Fetch data when modal opens
  React.useEffect(() => {
    if (dumpster) {
      dispatch(getDumpYardTemplates());
      dispatch(
        getOrders({
          pagination: { offset: 0, count: 100 },
          filters: {
            statuses: [
              OrderStatus.OrderStatusDelivered,
              OrderStatus.OrderStatusScheduledToPickup,
            ],
          },
        })
      );

      // Set initial values from dumpster
      setLocationType(dumpster.locationType || "unknown");
      setSelectedOrderId(dumpster.currentOrderId || "");
      setSelectedStorageId(dumpster.currentStorageYardId || "");
    }
  }, [dumpster, dispatch]);

  React.useEffect(() => {
    if (!setLocationAPI.loading && !!setLocationAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (
      !setLocationAPI.loading &&
      !setLocationAPI.error &&
      setLocationAPI.value
    ) {
      toast.success("Location updated successfully.");
    }
  }, [
    setLocationAPI.loading,
    setLocationAPI.error,
    setLocationAPI.value,
    dispatch,
  ]);

  const handleClose = () => {
    dispatch(resetShowSetLocationModal());
    setLocationType("unknown");
    setSelectedOrderId("");
    setSelectedStorageId("");
  };

  const handleSubmit = () => {
    if (!dumpster) return;

    let address = "";
    let city = "";
    let zip = "";
    let orderId: string | undefined;
    let storageYardId: string | undefined;

    if (locationType === "order" && selectedOrderId) {
      const order = activeOrders.find((o) => o.id === selectedOrderId);
      if (order) {
        address = order.address;
        city = order.city;
        zip = order.zip;
        orderId = order.id;
      }
    } else if (locationType === "storage" && selectedStorageId) {
      const storage = storageYards.find((s) => s.id === selectedStorageId);
      if (storage) {
        address = storage.address;
        city = storage.city;
        zip = storage.zip;
        storageYardId = storage.id;
      }
    }

    dispatch(
      setDumpsterLocation({
        dumpsterId: dumpster.id,
        locationType,
        orderId,
        storageYardId,
        address,
        city,
        zip,
      })
    );
  };

  const isValid = () => {
    if (locationType === "unknown") return true;
    if (locationType === "on_truck") return true;
    if (locationType === "order") return !!selectedOrderId;
    if (locationType === "storage") return !!selectedStorageId;
    return false;
  };

  if (!dumpster) return null;

  const selectedOrder = activeOrders.find((o) => o.id === selectedOrderId);
  const selectedStorage = storageYards.find((s) => s.id === selectedStorageId);

  return (
    <Modal
      isOpen={!!dumpster}
      onRequestClose={handleClose}
      style={modalStyles}
    >
      <div className="set-location-modal-component">
        <Close className="close-button" onClick={handleClose} />

        <div className="title">Set Dumpster Location</div>
        <div className="dumpster-label">{dumpster.label}</div>

        <div className="form-content">
          <div className="field-container">
            <div className="label">Location Type</div>
            <select
              value={locationType}
              onChange={(e) => {
                setLocationType(e.target.value as DumpsterLocationType);
                setSelectedOrderId("");
                setSelectedStorageId("");
              }}
              className="select-input"
            >
              <option value="unknown">Unknown</option>
              <option value="storage">Storage</option>
              <option value="order">Order</option>
              <option value="on_truck">On Truck</option>
            </select>
          </div>

          {locationType === "storage" && (
            <div className="field-container">
              <div className="label">Select Storage</div>
              <select
                value={selectedStorageId}
                onChange={(e) => setSelectedStorageId(e.target.value)}
                className="select-input"
              >
                <option value="">-- Select storage --</option>
                {storageYards.map((yard) => (
                  <option key={yard.id} value={yard.id}>
                    {yard.name}
                  </option>
                ))}
              </select>
              {selectedStorage && (
                <div className="selected-address">
                  <div className="address-label">Address:</div>
                  <div className="address-value">
                    {selectedStorage.address}
                    <br />
                    {selectedStorage.city}, {selectedStorage.zip}
                  </div>
                </div>
              )}
            </div>
          )}

          {locationType === "order" && (
            <div className="field-container">
              <div className="label">Select Order</div>
              <select
                value={selectedOrderId}
                onChange={(e) => setSelectedOrderId(e.target.value)}
                className="select-input"
              >
                <option value="">-- Select an order --</option>
                {activeOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.name} - {order.address}, {order.city}
                  </option>
                ))}
              </select>
              {selectedOrder && (
                <div className="selected-address">
                  <div className="address-label">Address:</div>
                  <div className="address-value">
                    {selectedOrder.address}
                    <br />
                    {selectedOrder.city}, {selectedOrder.zip}
                  </div>
                </div>
              )}
              {activeOrders.length === 0 && (
                <div className="no-options">
                  No active orders with dumpsters on site.
                </div>
              )}
            </div>
          )}

          <div className="button-row">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={setLocationAPI.loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="submit-button"
              onClick={handleSubmit}
              disabled={setLocationAPI.loading || !isValid()}
            >
              {setLocationAPI.loading ? (
                <>
                  Saving...
                  <Oval
                    color="#fff"
                    height={16}
                    width={16}
                    secondaryColor="#ccc"
                  />
                </>
              ) : (
                "Save Location"
              )}
            </button>
          </div>

          {!!setLocationAPI.error && (
            <div className="error-text">
              Something went wrong. Please try again.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
